/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import React, { useState, useContext, useEffect } from 'react';
import flatMap from 'lodash/flatMap';

import { AuthContext } from '../authentication/AuthContext';

import { useEntityContext } from '../entities/EntityContext';
import {
  fetchAllProductCategories,
  updateProductCategory as _updateProductCategory,
  createProductCategory as _createProductCategory,
  mapProductCategoryRequestBody,
} from './productCategoryService';

const ProductCategoriesContext = React.createContext(undefined);

export function ProductCategoriesProvider({ children }) {
  const { appendEntities } = useEntityContext();
  const [productCategoriesMap, setProductCategoriesMap] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);
  const { state: authState } = useContext(AuthContext);

  async function fetchData() {
    if (isLoaded) {
      return;
    }

    setIsLoaded(false);
    await fetchAllProductCategories()
      .then(({ data }) => {
        const d = getProductCategoriesMap(data);
        setProductCategoriesMap(d);
        setIsLoaded(true);
      })
      .catch((err) => {
        alert('There was an error fetching product categories');
        throw err;
      });
  }

  useEffect(() => {
    if (authState) {
      fetchData();
    }
  }, [authState]);

  useEffect(() => {
    if (!authState || !isLoaded) {
      return;
    }

    const globalProductCategories = Object.values(productCategoriesMap);

    appendEntities({
      entityName: 'productCategory',
      entityKey: 'id',
      entities: globalProductCategories,
    });
  }, [isLoaded]);

  async function updateProductCategory(id, data) {
    await _updateProductCategory(id, mapProductCategoryRequestBody(data));
    await fetchData();
  }

  async function createProductCategory(data) {
    const res = await _createProductCategory(
      mapProductCategoryRequestBody(data),
    );
    await fetchData();
    return res.data;
  }

  return (
    <ProductCategoriesContext.Provider
      value={{
        isLoaded,
        productCategoriesMap,
        createProductCategory,
        updateProductCategory,
      }}
    >
      {children}
    </ProductCategoriesContext.Provider>
  );
}

function getProductCategoriesMap(productCategories = []) {
  const productSubcategories =
    flatMap(productCategories, (d) => d.productSubcategories) || [];

  const productClasses =
    flatMap(productSubcategories, (d) => d.productClasses) || [];

  const productSubclasses =
    flatMap(productClasses, (d) => d.productSubclasses) || [];

  const globalProductCategories = [
    ...productCategories.map((pc) => ({ ...pc, isCategory: true })),
    ...productSubcategories,
    ...productClasses,
    ...productSubclasses,
  ];

  const productCategoriesMap = globalProductCategories.reduce((acc, curr) => {
    acc[curr.id] = curr;
    return acc;
  }, {});

  return productCategoriesMap;
}

export function useProductCategories() {
  const {
    isLoaded,
    productCategoriesMap,
    updateProductCategory,
    createProductCategory,
  } = useContext(ProductCategoriesContext);

  function findProductCategoryById(id) {
    return productCategoriesMap[id];
  }

  function getAllCategories() {
    return Object.values(productCategoriesMap).filter((pc) => pc.isCategory);
  }

  return {
    isLoaded,
    findProductCategoryById,
    updateProductCategory,
    createProductCategory,
    getAllCategories,
  };
}
